import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import Typography, { type TypographyProps } from '@mui/material/Typography'
import { type SxProps, type Theme } from '@mui/material/styles'
import { visuallyHidden } from '@mui/utils'
import { userLabel, userLabelTargetProps, type Person } from '../utilities/userLabel'

type Props = {
  user: Person
  /** Adds "(you)" after the label, for the signed-in user. */
  you?: boolean
  /** Shown when the person has no name, no email and no id. */
  fallback?: string
  variant?: TypographyProps['variant']
  /** One line, cut with an ellipsis. */
  noWrap?: boolean
  sx?: SxProps<Theme>
}

/**
 * THE way the app shows a person (app#401): the name, with the email on hover; the email alone for
 * a person with no name; the email marked "not a member" for an outsider; a pattern as typed. The
 * rules are in `utilities/userLabel.ts`.
 *
 * ⚠ The email is not only in the tooltip. A tooltip opens on mouse hover and keyboard focus, so the
 * label takes focus when it has one. A screen reader reads the email from visually hidden text, and
 * the tooltip uses `describeChild`: without it MUI puts the email in `aria-label`, which REPLACES
 * the name for a screen reader. `userLabelTargetProps` removes the `title` and `aria-describedby`
 * that `describeChild` adds, so the email is read once.
 */
export default function UserLabel({ user, you, fallback = '', variant = 'body2', noWrap, sx }: Readonly<Props>) {
  const label = userLabel(user)
  const text = label.text || fallback

  const body = (
    <Typography
      component="span"
      variant={variant}
      noWrap={noWrap}
      {...userLabelTargetProps(label)}
      sx={[{ display: noWrap ? 'block' : 'inline', wordBreak: noWrap ? undefined : 'break-word' }, ...(sx === undefined ? [] : Array.isArray(sx) ? sx : [sx])]}
    >
      {text}
      {label.hover && <Box component="span" sx={visuallyHidden}>{` (${label.hover})`}</Box>}
      {label.outsider && (
        <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>
          {' '}
          (not a member)
        </Typography>
      )}
      {you && (
        <Typography component="span" variant="caption" sx={{ color: 'text.disabled' }}>
          {' '}
          (you)
        </Typography>
      )}
    </Typography>
  )

  if (!label.hover) return body
  return (
    <Tooltip title={label.hover} placement="top" arrow describeChild>
      {body}
    </Tooltip>
  )
}
